import katex from "katex";
import "katex/dist/katex.min.css";

// Render prose that may contain LaTeX math delimited by `$…$`/`$$…$$` OR the
// LaTeX-native `\(…\)` (inline) / `\[…\]` (block) — the generator + the lecture
// explainer emit both styles. We also recognise bare `\begin{env}…\end{env}`
// AMS environments (align*, cases, matrix, …) emitted WITHOUT delimiters. We
// split on all of these and hand each math span to KaTeX (errors fall back to
// the raw source rather than throwing). Plain segments render as text; newlines
// are preserved.

type Seg = { type: "text" | "inline" | "block"; value: string };

// Restore LaTeX commands whose leading backslash was swallowed into a control
// char by lenient JSON parsing upstream (e.g. `\text` → a literal TAB + "ext",
// which KaTeX then renders as "extdx"). Mirror of the backend's D.6
// `_fix_latex_controls`: only a control char IMMEDIATELY followed by a letter is
// touched, so real newlines / indentation / prose are never altered.
const CTRL_TO_ESC: Record<string, string> = {
  "\b": "\\b",
  "\t": "\\t",
  "\v": "\\v",
  "\f": "\\f",
  "\r": "\\r",
};
function repairLatexControls(input: string): string {
  return (
    input
      .replace(/[\b\t\v\f\r](?=[A-Za-z])/g, (c) => CTRL_TO_ESC[c] ?? c)
      // Strip any REMAINING stray control chars. Corruption like `\t\f0` decodes
      // to TAB+FF+"0": the control chars are followed by another control / a
      // digit (not a letter), so the mapping above skips them and they leak to
      // KaTeX — rendered as a box glyph + a parse error (the whole line turns
      // red). A raw control char is never legitimate in generated math/prose.
      .replace(/[\b\t\v\f\r]/g, "")
  );
}

// Collapse an over-escaped command — a doubled backslash IMMEDIATELY followed by
// a letter (`\\vec`, `\\begin`, `\\nabla`) — back to a single backslash. The
// generator sometimes double-escapes LaTeX, which leaves `\\command` literals in
// the JSON. A genuine LaTeX line break `\\` is followed by whitespace / `&` /
// newline (never a letter), so align-row separators are preserved.
function collapseOverEscaped(input: string): string {
  return input.replace(/\\\\(?=[A-Za-z])/g, "\\");
}

// Full normalisation applied before any math parsing / rendering. Exported so the
// markdown-aware `RichText` renderer applies the SAME JSON-control-char repair
// before handing prose to remark/rehype (marking answers flow through the same
// lenient-JSON decode path that corrupts `\text`/`\\vec`).
export function normalizeTex(raw: string): string {
  return collapseOverEscaped(repairLatexControls(raw));
}

// A brace group allowing one level of nesting, e.g. `{1}`, `{\vec{k}}`.
const GROUP = String.raw`\{(?:[^{}]|\{[^{}]*\})*\}`;
// A sub/superscript: `_{…}` / `^{…}` (braced) or `_x` / `^2` / `_\alpha` (single).
const SCRIPT = String.raw`[_^](?:${GROUP}|\\?[A-Za-z0-9])`;
// A run of bare (undelimited) LaTeX: a command + any brace-group args + any
// chained scripts, e.g. `\text{singular}`, `\frac{1}{\hbar}`, `\nabla_{\vec{k}}`,
// `\sum_{i=0}^{n}`. The generator often emits these in atomic fields (fill-blank
// chips, definition terms, answers) and inline equation prose WITHOUT `$…$`, so
// we KaTeX them inline even outside math delimiters.
const BARE_TEX = new RegExp(`\\\\[a-zA-Z]+(?:${GROUP})*(?:${SCRIPT})*`, "g");

// Split a plain-text run into text + inline-math sub-segments by scanning for
// bare LaTeX command runs. Text between/around commands stays text.
function pushTextWithBareTex(segs: Seg[], text: string): void {
  let last = 0;
  let m: RegExpExecArray | null;
  BARE_TEX.lastIndex = 0;
  while ((m = BARE_TEX.exec(text)) !== null) {
    if (m.index > last) segs.push({ type: "text", value: text.slice(last, m.index) });
    segs.push({ type: "inline", value: m[0] });
    last = BARE_TEX.lastIndex;
  }
  if (last < text.length) segs.push({ type: "text", value: text.slice(last) });
}

function parseSegments(raw: string): Seg[] {
  const input = normalizeTex(raw);
  const segs: Seg[] = [];
  // Order: bare `\begin{env}…\end{env}` block, `$$…$$` block, `\[…\]` block,
  // `$…$` inline, `\(…\)` inline. The environment branch captures the WHOLE
  // `\begin…\end` so KaTeX renders the environment as one display unit.
  //
  // Currency guard on the inline `$…$` branch: the opening `$` must NOT be
  // immediately followed by a digit — otherwise prose like "it costs $5 and $10"
  // parses "$5 and $" as math and eats the money. Real inline math opens with a
  // letter / command / operator (`$x$`, `$\rho$`, `$-1$`), never a bare digit, so
  // the `(?!\d)` lookahead is a safe minimal fix. The closing `$` must still be on
  // the same line (`[^$\n]+?`), so a lone mid-word `$` never opens a span at all.
  const re =
    /(\\begin\{[a-zA-Z*]+\}[\s\S]*?\\end\{[a-zA-Z*]+\})|\$\$([\s\S]+?)\$\$|\\\[([\s\S]+?)\\\]|\$(?!\d)([^$\n]+?)\$|\\\(([\s\S]+?)\\\)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(input)) !== null) {
    if (m.index > last) pushTextWithBareTex(segs, input.slice(last, m.index));
    if (m[1] !== undefined) segs.push({ type: "block", value: m[1] });
    else if (m[2] !== undefined) segs.push({ type: "block", value: m[2] });
    else if (m[3] !== undefined) segs.push({ type: "block", value: m[3] });
    else if (m[4] !== undefined) segs.push({ type: "inline", value: m[4] });
    else segs.push({ type: "inline", value: m[5] ?? "" });
    last = re.lastIndex;
  }
  if (last < input.length) pushTextWithBareTex(segs, input.slice(last));
  return segs;
}

// Strip ONE surrounding pair of math delimiters (`$$…$$`, `$…$`, `\[…\]`,
// `\(…\)`) from a string that is meant to be rendered as a single KaTeX
// expression. The generator sometimes wraps atomic LaTeX fields (e.g.
// equation-builder pool tiles) in `$$…$$`; feeding those straight to KaTeX
// renders the literal `$` characters. iOS's `MathText(rawLaTeX:)` strips them,
// so we mirror that. Only used by `RawMath` — NOT by `normalizeTex`, since
// `parseSegments` relies on the delimiters to locate math spans. We refuse to
// strip when the inner text still holds a same-kind delimiter (e.g. the
// multi-span `$a$ + $b$`), which would be a mixed prose/math string.
function stripOuterMathDelimiters(s: string): string {
  const t = s.trim();
  const pairs: [string, string][] = [
    ["$$", "$$"],
    ["\\[", "\\]"],
    ["\\(", "\\)"],
    ["$", "$"],
  ];
  for (const [open, close] of pairs) {
    if (t.length >= open.length + close.length && t.startsWith(open) && t.endsWith(close)) {
      const inner = t.slice(open.length, t.length - close.length);
      if ((open === "$" || open === "$$") && inner.includes(open)) continue;
      return inner.trim();
    }
  }
  return t;
}

function renderMath(tex: string, display: boolean): string {
  try {
    return katex.renderToString(tex, { throwOnError: false, displayMode: display });
  } catch {
    return display ? `$$${tex}$$` : `$${tex}$`;
  }
}

export function MathText({ text, className }: { text: string; className?: string }) {
  if (!text) return null;
  const segs = parseSegments(text);
  return (
    <span className={className} style={{ whiteSpace: "pre-wrap" }}>
      {segs.map((s, i) =>
        s.type === "text" ? (
          <span key={i}>{s.value}</span>
        ) : (
          <span
            key={i}
            dangerouslySetInnerHTML={{ __html: renderMath(s.value, s.type === "block") }}
          />
        ),
      )}
    </span>
  );
}

// Render a string that is ENTIRELY raw LaTeX (no `$…$` delimiters) as one KaTeX
// expression — the web mirror of iOS `MathText(rawLaTeX:)`. Used for equation
// tiles, equation-template segments, and derivation target statements, where the
// generator emits bare LaTeX with subscripts / nested fractions that the
// command-by-command bare-tex path can't stitch together. Falls back to the raw
// source on a KaTeX error.
export function RawMath({
  text,
  className,
  display,
}: {
  text: string;
  className?: string;
  display?: boolean;
}) {
  if (!text) return null;
  const norm = normalizeTex(text);
  const tex = stripOuterMathDelimiters(norm);
  // If a math delimiter survives the outer strip, this is NOT a single equation —
  // it's mixed prose + math (some derivation `latex` fields are whole sentences
  // with several `$…$` spans). Feeding that to KaTeX as one expression fails on
  // the leading `$`; render it through MathText, which splits + renders each span.
  if (/\$|\\\[|\\\(/.test(tex)) {
    return <MathText text={norm} className={className} />;
  }
  return (
    <span
      className={className}
      dangerouslySetInnerHTML={{ __html: renderMath(tex, !!display) }}
    />
  );
}
